import React from 'react'
import { formatBigMoney, formatMoney, formatPercentage } from '../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'
import { lowercaseFirstLetter } from '../../utils/stringConversion'
import { getPreviousSubscriptionPeriods, getSubscriptionRate } from '../warrants/WarrantOutcome'
import { NewsItem } from 'types/NewsItem'

interface NewsHeadlineOutcomeProps {
    locale: string
    row: NewsItem
}

const getNewsHeadline = (news: NewsItem, locale: string, typeTranslated?: string) => {
    let headline = ''

    const { companyName, currency, eventType, issueData, warrantData } = news
    if (issueData) {
        const { unitSubTotal, unitSubPrice, unitPctSubGuarantor, unitSubGuarantor, type } = issueData
        const secondaryOffering = issueData.secondaryOffering !== null ? issueData.secondaryOffering : 0
        const secondarySub = issueData.secondarySub !== null ? issueData.secondarySub : 0
        const totalOfferedUnits = (issueData.offeredUnits ?? 0) + (issueData.offeredUnitsUnlisted ?? 0)

        const subRate = formatPercentage(
            (100 * ((unitSubTotal ?? 0) + secondarySub)) / (totalOfferedUnits + secondaryOffering),
            {
                locale,
                decimals: 0,
            },
        )
        const guarantorsPart =
            (100 * (unitSubGuarantor || 0)) / (unitSubTotal ?? 0) +
            ((unitPctSubGuarantor || 0) * (totalOfferedUnits || 0)) / (unitSubTotal ?? 0)

        if (type === 'Rights issue') {
            if (eventType === 'preliminary outcome') {
                headline = `${companyName} preliminärt utfall: `
            } else {
                headline = `${companyName} utfall: `
            }

            headline += `Teckningsgrad ${subRate}`

            if (guarantorsPart) {
                headline += ` där garanter stod för ${formatPercentage(guarantorsPart, {
                    locale,
                    decimals: 0,
                })} av de tecknade aktierna`
            }
        } else if (type === 'ABB Directed issue') {
            headline = `${companyName} genomförde en ${typeTranslated} på teckningskurs ${formatMoney(
                unitSubPrice || 0,
                {
                    locale,
                    currencySymbol: currency,
                },
            )} och inbringade ${formatBigMoney(+(unitSubPrice || 0) * (unitSubTotal ?? 0), {
                locale,
                currencySymbol: currency,
            })}`
        } else if (type === 'IPO') {
            headline = `Utfallet i ${companyName} IPO: Teckningsgrad ${subRate}`
        }
    } else if (warrantData) {
        const subscriptionPeriods = getPreviousSubscriptionPeriods(warrantData)
        const sortedSubscriptionPeriods = subscriptionPeriods.sort(
            (a, b) => new Date(b.subEndDate).getTime() - new Date(a.subEndDate).getTime(),
        )
        if (sortedSubscriptionPeriods.length > 0 && sortedSubscriptionPeriods[0].sharesSubscribed !== null) {
            headline = `${companyName} utfall i optionsinlösen för serie ${news.name || warrantData.name}: Teckningsgrad ${formatPercentage(getSubscriptionRate(warrantData, sortedSubscriptionPeriods[0]), { locale, decimals: 0 })}`
        }
    }
    return headline
}

function NewsHeadlineOutcome({ row, locale }: NewsHeadlineOutcomeProps) {
    const { t } = useTranslation()

    const typeTranslated = row.issueData
        ? lowercaseFirstLetter(t(`entities.issueType.values.${row.issueData.type}`))
        : undefined

    const newsHeadline = getNewsHeadline(row, locale, typeTranslated)

    return <span>{newsHeadline}</span>
}

export default NewsHeadlineOutcome
